import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCustomers } from "@/hooks/use-customers";
import { useCustomerOpportunities } from "@/hooks/use-opportunities";
import { useCreateFollowUp } from "@/hooks/use-follow-up-mutations";
import { PRIORITY_LABELS } from "@/lib/follow-up-calc";
import { FOLLOW_UP_CHANNEL_LABELS } from "@/lib/follow-up-labels";
import { isOpenStage, STAGE_LABELS } from "@/lib/opportunity-calc";
import { osloDateString } from "@/lib/oslo-time";
import type { FollowUpChannel, FollowUpPriority } from "@/lib/db-types";

const NONE = "none";

interface NewFollowUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Satt fra kundekortet; utelatt = brukeren velger kunde. */
  customerId?: string;
  defaultOpportunityId?: string;
}

export default function NewFollowUpDialog({ open, onOpenChange, customerId, defaultOpportunityId }: NewFollowUpDialogProps) {
  const create = useCreateFollowUp();
  const { data: customers = [] } = useCustomers();

  const [pickedCustomer, setPickedCustomer] = useState("");
  const effectiveCustomerId = customerId ?? pickedCustomer;
  const { data: opportunities = [] } = useCustomerOpportunities(open && effectiveCustomerId ? effectiveCustomerId : undefined);
  const openOpportunities = opportunities.filter((o) => isOpenStage(o.stage));

  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState<FollowUpPriority>("this_week");
  const [channel, setChannel] = useState<string>(NONE);
  const [opportunityId, setOpportunityId] = useState<string>(NONE);

  useEffect(() => {
    if (!open) return;
    setPickedCustomer("");
    setTitle("");
    setDue(osloDateString(new Date()));
    setPriority("this_week");
    setChannel(NONE);
    setOpportunityId(defaultOpportunityId ?? NONE);
  }, [open, defaultOpportunityId]);

  const valid = !!effectiveCustomerId && title.trim().length > 0 && !!due;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ny oppfølging</DialogTitle>
          <DialogDescription>Du blir eier av oppfølgingen og finner den i køen din.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            create.mutate(
              {
                customer_id: effectiveCustomerId,
                title,
                due_date: due,
                priority,
                channel: channel === NONE ? null : (channel as FollowUpChannel),
                opportunity_id: opportunityId === NONE ? null : opportunityId,
              },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
        >
          <div className="space-y-4 py-2">
            {!customerId && (
              <div className="space-y-1.5">
                <Label htmlFor="new-follow-up-customer">Kunde</Label>
                <Select value={pickedCustomer} onValueChange={setPickedCustomer}>
                  <SelectTrigger id="new-follow-up-customer" aria-label="Kunde">
                    <SelectValue placeholder="Velg kunde" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="new-follow-up-title">Tittel</Label>
              <Input id="new-follow-up-title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus={!!customerId} />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-follow-up-due">Forfall</Label>
                <Input id="new-follow-up-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-follow-up-priority">Prioritet</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as FollowUpPriority)}>
                  <SelectTrigger id="new-follow-up-priority" aria-label="Prioritet">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PRIORITY_LABELS) as FollowUpPriority[]).map((p) => (
                      <SelectItem key={p} value={p}>
                        {PRIORITY_LABELS[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-follow-up-channel">Kanal</Label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger id="new-follow-up-channel" aria-label="Kanal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Ikke valgt</SelectItem>
                    {(Object.keys(FOLLOW_UP_CHANNEL_LABELS) as FollowUpChannel[]).map((c) => (
                      <SelectItem key={c} value={c}>
                        {FOLLOW_UP_CHANNEL_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-follow-up-opportunity">Koble til prosess</Label>
              <Select value={opportunityId} onValueChange={setOpportunityId} disabled={!effectiveCustomerId}>
                <SelectTrigger id="new-follow-up-opportunity" aria-label="Koble til prosess">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Ingen prosess</SelectItem>
                  {openOpportunities.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.title} ({STAGE_LABELS[o.stage]})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? "Lagrer ..." : "Lagre"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
